// Copying from the sidebar entries: the text a selection should put on the clipboard
// (a key and its value on one line, tab-separated, as table cells are; buttons and
// expand labels left out), and the bounds a double-click selects (no blank around).

const BLOCK = new Set([
  "DIV",
  "TABLE",
  "TBODY",
  "THEAD",
  "TFOOT",
  "TR",
  "TD",
  "TH",
  "P",
  "PRE",
  "DETAILS",
  "UL",
  "OL",
  "LI",
  "SECTION",
]);
const SKIPPED = new Set(["BUTTON", "SUMMARY", "TEXTAREA", "INPUT", "SCRIPT"]);
// Drawings, axis labels and counts: the section lists hold the data.
const SKIPPED_CLASSES = [
  "fe-bar",
  "fe-profile",
  "fe-ticks",
  "fe-ylab",
  "fe-count",
];

const kids = (node) => [...(node.childNodes ?? node.children ?? [])];
const hasClass = (node, name) =>
  typeof node.className === "string" &&
  node.className.split(/\s+/).includes(name);
const isRow = (node) => node.tagName === "TR" || hasClass(node, "kv");
// Not copied: controls, hidden rows (`also selected in` when empty), closed expands,
// drawings.
const skipped = (node) =>
  SKIPPED.has(node.tagName) ||
  node.style?.display === "none" ||
  (node.tagName === "DETAILS" && node.getAttribute("open") === null) ||
  SKIPPED_CLASSES.some((name) => hasClass(node, name));
// Slot cells copy as the tagged `a|b|c` value; a blank slot (shown `·`) is empty.
const slotText = (node) =>
  kids(node)
    .filter((cell) => cell.nodeType === 1)
    .map((cell) =>
      kids(cell).some((k) => k.nodeType === 1 && hasClass(k, "fe-blank"))
        ? ""
        : cell.textContent.trim(),
    )
    .join("|");
// Collapses blanks but keeps the tab between a key and its value.
const clean = (text) => text.replace(/[^\S\t]+/g, " ").trim();

function linesOf(node) {
  if (node.nodeType === 3) return [node.textContent];
  if (node.nodeType !== 1 && node.nodeType !== 11) return [];
  if (node.nodeType === 1 && skipped(node)) return [];
  if (node.nodeType === 1 && node.tagName === "PRE")
    return node.textContent.split("\n");
  if (node.nodeType === 1 && hasClass(node, "fe-slots"))
    return [slotText(node)];
  if (node.nodeType === 1 && isRow(node)) {
    const cells = kids(node)
      .filter((c) => c.nodeType === 1)
      .map((c) => linesOf(c).map(clean).filter(Boolean));
    if (cells.every((c) => c.length <= 1))
      return [cells.flat().filter(Boolean).join("\t")];
    return cells.flat();
  }
  const lines = [];
  let current = "";
  kids(node).forEach((child) => {
    const block =
      child.nodeType === 1 && (BLOCK.has(child.tagName) || isRow(child));
    const childLines = linesOf(child);
    if (!block && childLines.length <= 1) {
      current += childLines[0] ?? "";
      return;
    }
    lines.push(current, ...childLines);
    current = "";
  });
  lines.push(current);
  return lines;
}

/** @returns {string} the clipboard text of a node or a selection's cloned fragment */
export function plainTextOf(node) {
  return linesOf(node).map(clean).filter(Boolean).join("\n");
}

/**
 * @param {string[]} texts consecutive text node contents
 * @returns {{start: [number, number], end: [number, number]}|null} first non-blank
 *   character and the offset after the last one, as [text index, offset]
 */
export function trimmedBounds(texts) {
  const start = texts.findIndex((t) => /\S/.test(t));
  if (start < 0) return null;
  let end = texts.length - 1;
  while (!/\S/.test(texts[end])) end--;
  return {
    start: [start, texts[start].search(/\S/)],
    end: [end, texts[end].replace(/\s+$/, "").length],
  };
}
